import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Send, BookOpen, Copy, Check } from 'lucide-react';
import { sendAgentMessage, askAgent, fetchChatSuggestions, fetchAgentCapabilities } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { MarkdownMessage } from './markdown-message';
import {
  isAskShortcut, ASK_TRIGGER_CLASS, ASK_TRIGGER_KBD_CLASS, ASK_TRIGGER_LABEL_CLASS,
} from '@/config/shell';
import {
  historyKey, upsertConversation, removeConversation, formatWhen, drawerView, routeReply,
  loadHistory, saveHistory, recordReply, openFromStore, threadForSend, discardLegacyThread,
  mergeMessages, maxSeq, allocateSeq,
  type Conversation, type StoredMessage, type StorageLike,
} from '@/config/copilotHistory';
import { RailMark, DrawerMark } from '../brand/marks/Mark';
import { SuryaMark } from '../brand/SuryaMark';

type Message = StoredMessage & { data?: any };

/** The conversation on screen. `owner` is the history key it belongs to, fixed at creation,
 *  so a login switch can never file A's thread under B. */
interface Thread {
  id: string;
  startedAt: string;
  owner: string;
  messages: Message[];
}

const FALLBACK_SUGGESTIONS = [
  { category: 'Priorities', questions: ['What are my top 3 priorities today?', 'Which customers should I call this week?', 'What events are coming up that need prep?'] },
  { category: 'Stock & Supply', questions: ['Which SKUs are at stockout risk?', 'Show me overdue POs — which suppliers to chase?', 'Are there any buying window opportunities?'] },
  { category: 'Sales Intel', questions: ['Who are my highest-CLV dormant accounts?', 'Which customers are furthest behind budget?', 'Which proformas haven\'t converted?'] },
  { category: 'Products', questions: ['Which products are accelerating fastest?', 'Show products with compressing margins', 'What dead stock can I push to customers?'] },
  { category: 'Events', questions: ['Which customers typically order before Ramadan?', 'What stock do we need for the next event?', 'Show me seasonal buying patterns'] },
  { category: 'Market & FX', questions: ['What\'s our FX risk exposure?', 'How are commodity prices trending?', 'What\'s the worst-case FX scenario?'] },
  // SPEC-138: lead with the platform's differentiators — provenance (how fresh),
  // explainability (why did it change), and honest governance state (pending rulings).
  // These are the generic fallback; the backend serves page-aware chips over the top.
  { category: 'Explain & verify', questions: ['How fresh is the data behind these numbers?', 'Why did this SKU\'s forecast change?', 'Which governance rules are still waiting on a ruling?'] },
];

function getPageFromPath(pathname: string): string {
  if (pathname === '/') return 'overview';
  const segment = pathname.split('/').filter(Boolean)[0] || 'overview';
  return segment;
}

/** Prettify a raw tool name (e.g. get_stock_risk → Stock risk) for citation chips. */
function prettyToolName(name: string): string {
  const cleaned = name.replace(/^(get|fetch|query|list)_/, '').replace(/_/g, ' ').trim();
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch { /* fall through */ }
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function newThread(owner: string): Thread {
  return { id: newId(), startedAt: new Date().toISOString(), owner, messages: [] };
}

/* SPEC-514 storage: the saved log is the SOURCE OF TRUTH (config/copilotHistory). Every change
 * reads it, applies one pure step and writes it back, merging by message id — never a stale
 * in-memory copy — because AppShell mounts a desktop AND a mobile drawer that share the key.
 * Storage blocked or full → the chat still works; only the log is lost. */
const STORE: StorageLike = {
  getItem: (k) => localStorage.getItem(k),
  setItem: (k, v) => localStorage.setItem(k, v),
  removeItem: (k) => localStorage.removeItem(k),
};

/* Fix loop 1 (Codex P1-2): the user the MOUNTED drawer belongs to right now. A reply's
 * completion compares its asker against this — not against a ref in its own (possibly
 * unmounted) instance — before it may touch anything on screen. */
let activeUserKey = '';
/** Mounted drawers per user, so the key is cleared only when the user's LAST drawer unmounts
 *  (the desktop drawer stays mounted while the mobile one comes and goes). */
const mountedDrawers = new Map<string, number>();

/** Monotonic creation order for messages (both drawers share the module). Fix loop 5: before
 *  each allocation it is seeded from the OWNER's saved log, so it stays above every saved
 *  sequence across a reload or a backward clock correction. */
let lastSeq = 0;
function seedSeq(list: readonly Conversation[]): void {
  lastSeq = Math.max(lastSeq, maxSeq(list));
}

function newMessage(owner: string, role: 'user' | 'assistant', content: string, extra: Partial<Message> = {}): Message {
  seedSeq(loadHistory(STORE, owner));
  lastSeq = allocateSeq(lastSeq, Date.now());
  return { id: newId(), seq: lastSeq, role, content, ...extra };
}

function focusAskTrigger() {
  (document.querySelector('[data-tour="ask-suryaiq"]') as HTMLElement | null)?.focus();
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

const ICON_BUTTON =
  'grid place-items-center w-10 h-10 md:w-8 md:h-8 rounded-lg text-[var(--text-tertiary)] hover:text-[var(--text-primary)] ' +
  'hover:bg-[var(--bg-overlay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60 ' +
  'transition-colors motion-reduce:transition-none';

/** SPEC-514 — the top-bar trigger: the Ask-Anything mark + "Ask SuryaIQ" (from md) + the "/"
 *  key (from lg). Compact, fixed intrinsic width, never shrinks (config/shell class contract).
 *  The accessible name is the visible words (WCAG 2.5.3); aria-keyshortcuts names the key. */
export function AskSuryaIQButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-tour="ask-suryaiq"
      aria-label="Ask SuryaIQ"
      aria-keyshortcuts="/"
      title="Ask SuryaIQ about any product or number  ( / )"
      className={ASK_TRIGGER_CLASS}
    >
      <span
        aria-hidden="true"
        className="w-6 h-6 rounded-md grid place-items-center shrink-0 bg-[var(--bg-overlay)] text-[var(--text-primary)]"
      >
        <RailMark id="ask" size={16} />
      </span>
      <span className={ASK_TRIGGER_LABEL_CLASS}>Ask SuryaIQ</span>
      <kbd aria-hidden="true" className={ASK_TRIGGER_KBD_CLASS}>/</kbd>
    </button>
  );
}

interface ChatSidebarProps {
  open: boolean;
  onToggle: () => void;
  // SPEC-123: a page (e.g. a scorecard tile's "Ask the assistant") can hand a
  // question to the drawer. When set, the visible instance fills its input with it,
  // focuses, and calls onPrefillConsumed so it fires once.
  prefill?: string | null;
  onPrefillConsumed?: () => void;
  /** SPEC-514: told when the drawer is open AND in wide reading mode, so the top bar can sit
   *  left of the wider drawer instead of under it. */
  onWideChange?: (wide: boolean) => void;
}

export function ChatSidebar({ open, onToggle, prefill, onPrefillConsumed, onWideChange }: ChatSidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const storageKey = historyKey(user?.username);

  // SPEC-514: every opening starts a FRESH conversation; the last one is in "Previous chats".
  const [thread, setThread] = useState<Thread>(() => newThread(storageKey));
  const threadRef = useRef(thread);
  threadRef.current = thread;
  const [view, setView] = useState<'chat' | 'history'>('chat');
  // The Previous chats list and the half-typed draft, each tagged with the user they belong to.
  const [historyState, setHistoryState] = useState<{ owner: string; list: Conversation[] }>(
    () => ({ owner: storageKey, list: [] }),
  );
  const setHistory = (list: Conversation[], owner: string = storageKey) => setHistoryState({ owner, list });
  const [draftState, setDraftState] = useState<{ owner: string; text: string }>(() => ({ owner: storageKey, text: '' }));
  const setInput = (text: string) => setDraftState({ owner: storageKey, text });
  // Fix loop 1 (Codex P1-1): the drawer renders ONLY from drawerView, which shows nothing that
  // is not the signed-in user's — synchronously, in the render where the user changes. (AppShell
  // also remounts the drawer per user.)
  const shown = drawerView({
    threadOwner: thread.owner, messages: thread.messages,
    listOwner: historyState.owner, list: historyState.list,
    draftOwner: draftState.owner, draft: draftState.text,
  }, storageKey);
  const messages = shown.messages as Message[];
  const history = shown.list as Conversation[];
  const input = shown.draft;
  useLayoutEffect(() => {
    activeUserKey = storageKey;
    mountedDrawers.set(storageKey, (mountedDrawers.get(storageKey) ?? 0) + 1);
    return () => {
      const left = (mountedDrawers.get(storageKey) ?? 1) - 1;
      if (left > 0) mountedDrawers.set(storageKey, left);
      else mountedDrawers.delete(storageKey);
      if (left <= 0 && activeUserKey === storageKey) activeUserKey = '';
    };
  }, [storageKey]);
  // The threads with a question in flight. One question at a time PER thread: a slow answer
  // never locks a fresh chat (review F4); its reply still lands in its own thread.
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(() => new Set());
  const loadingHere = pendingIds.has(thread.id);
  // One live region for the whole drawer, set only by a reply arriving (review F7).
  const [announcement, setAnnouncement] = useState('');
  const viewRef = useRef(view);
  viewRef.current = view;
  // SPEC-114: wide reading mode for planner sessions — persists for the session.
  const [expanded, setExpanded] = useState(
    () => sessionStorage.getItem('suryaiq_chat_expanded') === '1',
  );
  useEffect(() => {
    sessionStorage.setItem('suryaiq_chat_expanded', expanded ? '1' : '0');
  }, [expanded]);
  useEffect(() => {
    onWideChange?.(open && expanded);
  }, [open, expanded, onWideChange]);
  // SPEC-114: elapsed-seconds counter + copy-answer feedback.
  const [elapsed, setElapsed] = useState(0);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  // SPEC-108/131: planner explainability mode — only offered to planning-capable accounts.
  // The verdict is the SERVER's own `_planner_authorized` result, fetched from
  // /agent/capabilities, NOT a hand-copied role array. This makes the UI toggle and the
  // backend gate a single source, so the drawer can never show "Planner" for an account
  // the server then denies (UI says "Planner" but answers come back general/uncited).
  // Defaults false until the fetch resolves — fail-closed (never offer what may be denied).
  const [canPlanner, setCanPlanner] = useState(false);
  useEffect(() => {
    let live = true;
    setCanPlanner(false); // reset on account switch so A's verdict never leaks to B
    fetchAgentCapabilities()
      .then((c) => { if (live) setCanPlanner(Boolean(c?.planner_authorized)); })
      .catch(() => { if (live) setCanPlanner(false); });
    return () => { live = false; };
  }, [user?.username]);
  const [plannerMode, setPlannerMode] = useState(
    () => localStorage.getItem('suryaiq_planner_mode') === '1',
  );
  useEffect(() => {
    localStorage.setItem('suryaiq_planner_mode', plannerMode ? '1' : '0');
  }, [plannerMode]);
  const plannerActive = plannerMode && canPlanner;
  const [suggestions, setSuggestions] = useState<{ category: string; questions: string[] }[]>(FALLBACK_SUGGESTIONS);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Fetch context-aware suggestions when page changes
  useEffect(() => {
    const page = getPageFromPath(location.pathname);
    fetchChatSuggestions(page)
      .then((data) => {
        if (data?.suggestions && data.suggestions.length > 0) {
          setSuggestions(data.suggestions);
        } else {
          setSuggestions(FALLBACK_SUGGESTIONS);
        }
      })
      .catch(() => setSuggestions(FALLBACK_SUGGESTIONS));
  }, [location.pathname]);

  // The pre-SPEC-514 single shared key has no recorded owner: it is deleted, never migrated
  // into whoever signs in (Codex P1-3).
  useEffect(() => {
    discardLegacyThread(STORE);
  }, []);
  // A login switch (if this drawer were not remounted) drops every piece of the old user's state.
  useEffect(() => {
    setThread((t) => (t.owner === storageKey ? t : newThread(storageKey)));
    setHistoryState((h) => (h.owner === storageKey ? h : { owner: storageKey, list: [] }));
    setDraftState((d) => (d.owner === storageKey ? d : { owner: storageKey, text: '' }));
    setView('chat');
  }, [storageKey]);

  // SPEC-514: the thread on screen is written to the log as it grows (a closed tab loses
  // nothing). A thread with no question is never logged (copilotHistory.upsertConversation).
  // Only a CHANGED thread is written, so merely reopening an old chat never re-dates it.
  const persistedLength = useRef(new Map<string, number>());
  useEffect(() => {
    if (!thread.messages.some((m) => m.role === 'user')) return;
    if (persistedLength.current.get(thread.id) === thread.messages.length) return;
    persistedLength.current.set(thread.id, thread.messages.length);
    saveHistory(STORE, thread.owner, (h) => upsertConversation(h, thread, new Date().toISOString()));
  }, [thread]);

  // The mobile drawer UNMOUNTS on close: return focus from it before its nodes go.
  useLayoutEffect(() => () => {
    const panel = panelRef.current;
    if (panel && panel.contains(document.activeElement)) setTimeout(focusAskTrigger, 0);
  }, []);

  // SPEC-514: opening the drawer starts a fresh conversation.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setThread(newThread(storageKey));
      setView('chat');
      setCopiedIdx(null);
      setAnnouncement('');
    }
    if (!open && wasOpen.current) {
      // Focus was inside the drawer, which is now hidden: return it to the Ask trigger
      // (it renders again once the drawer closes). Review F6.
      const panel = panelRef.current;
      const active = document.activeElement;
      if (panel && panel.offsetWidth > 0 && (!active || active === document.body || panel.contains(active))) {
        setTimeout(focusAskTrigger, 0);
      }
    }
    wasOpen.current = open;
  }, [open, storageKey]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }, [messages, loadingHere]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  // SPEC-123: consume a prefill handed in by a page (e.g. a scorecard tile). Fill the
  // input (don't auto-send — the planner reviews before asking), focus it, and clear
  // the pending prefill so it fires exactly once. The mobile + desktop instances both
  // receive it; each fills its own (breakpoint-hidden) input harmlessly.
  useEffect(() => {
    if (prefill == null) return;
    setInput(prefill);
    setView('chat');
    setTimeout(() => inputRef.current?.focus(), 300);
    onPrefillConsumed?.();
  }, [prefill, onPrefillConsumed]);

  // SPEC-114: tick an elapsed-seconds counter while awaiting a response.
  useEffect(() => {
    if (!loadingHere) {
      setElapsed(0);
      return;
    }
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [loadingHere]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // AppShell mounts a desktop AND a mobile ChatSidebar simultaneously (each
      // hidden at the other breakpoint via display:none). Only the instance that
      // is actually visible should handle keys, else Esc double-fires. A
      // display:none instance has offsetWidth 0 (unaffected by position:fixed).
      if (panelRef.current && panelRef.current.offsetWidth === 0) return;
      // SPEC-509: "/" (shown on the Ask field) opens the assistant unless the reader is typing
      // in a field, focus is inside a dialog/overlay, a modal is open, or another handler
      // already took the key — the rule is config/shell.isAskShortcut, unit-tested. The
      // focused element is read from document.activeElement (an overlay may route keys to
      // the document while focus sits on one of its buttons).
      if (!open && isAskShortcut({
        key: e.key, ctrlKey: e.ctrlKey, metaKey: e.metaKey, altKey: e.altKey,
        defaultPrevented: e.defaultPrevented,
        modalOpen: !!document.querySelector('[aria-modal="true"]'),
        target: (document.activeElement as HTMLElement | null) ?? (e.target as HTMLElement | null),
      })) {
        e.preventDefault();
        onToggle();
      }
      if (e.key === 'Escape' && open) {
        // SPEC-114: on desktop, Esc first collapses wide mode, then closes.
        // On mobile (where expand is unavailable) Esc always closes.
        const isDesktop = window.matchMedia('(min-width: 768px)').matches;
        if (expanded && isDesktop) {
          setExpanded(false);
        } else {
          onToggle();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onToggle, expanded]);

  const send = useCallback(async (text: string) => {
    const current = threadRef.current;
    if (!text.trim() || pendingIds.has(current.id) || current.owner !== storageKey) return;
    const tid = current.id;
    const owner = current.owner;
    // The send continues from the SAVED conversation merged with the screen, so a reply another
    // drawer saved (the mobile drawer unmounts on close) is in the context and never
    // overwritten (Codex P1-4).
    const base = threadForSend(STORE, current) as Message[];
    const newMessages: Message[] = [...base, newMessage(owner, 'user', text)];
    setThread({ ...current, messages: newMessages });
    setInput('');
    setPendingIds((prev) => new Set(prev).add(tid));

    // A reply is SAVED first, always, under the user who asked — so it survives the drawer
    // closing, a clear, a reopen or a login switch (review F1). It is SHOWN only while that user
    // is still the signed-in one and still on that conversation (Codex P1-2).
    const deliver = (reply: Message) => {
      const list = recordReply(STORE, { owner, threadId: tid }, reply, new Date().toISOString());
      const route = routeReply(
        { owner, threadId: tid },
        { userKey: activeUserKey, threadId: threadRef.current.id, view: viewRef.current },
      );
      if (route.refreshList) setHistory(list, owner);
      if (route.renderInThread) {
        setThread((prev) => (prev.id === tid && prev.owner === owner
          ? { ...prev, messages: mergeMessages(prev.messages, [reply]) as Message[] } : prev));
        setAnnouncement(reply.content.startsWith('Error:') ? 'SuryaIQ could not answer. The error is in the chat.' : 'SuryaIQ answered.');
      }
    };

    try {
      const apiMessages = newMessages.map((m) => ({ role: m.role, content: m.content }));
      const res = await sendAgentMessage(
        apiMessages,
        undefined,
        plannerMode && canPlanner ? 'planner' : undefined,
      );
      deliver(newMessage(owner, 'assistant', res.message?.content || res.answer || 'No response', {
        data: res.data,
        tools_used: Array.isArray(res.tools_used)
          ? res.tools_used.filter((t: unknown): t is string => typeof t === 'string') : undefined,
      }));
    } catch {
      try {
        const res = await askAgent(text);
        deliver(newMessage(owner, 'assistant', res.answer, { data: res.data }));
      } catch (err: any) {
        deliver(newMessage(owner, 'assistant', `Error: ${err.message}`));
      }
    }
    setPendingIds((prev) => {
      const next = new Set(prev);
      next.delete(tid);
      return next;
    });
  }, [pendingIds, plannerMode, canPlanner, storageKey]);

  // SPEC-514: the bin clears THIS chat off the screen only. It stays in Previous chats (the
  // log of what was spoken about); a chat is removed from the log only from that list.
  const clearChat = () => {
    setThread(newThread(storageKey));
    setCopiedIdx(null);
    setView('chat');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const showHistory = () => {
    if (view === 'history') {
      setView('chat');
      return;
    }
    setHistory(loadHistory(STORE, storageKey));
    setView('history');
  };

  const reopen = (c: Conversation) => {
    // The list is a snapshot: open the copy saved in THIS user's log now (review F2, Codex
    // P1-2/P1-4). Not there (deleted, or not this user's) → refresh the list, open nothing.
    const fresh = openFromStore(STORE, storageKey, c.id);
    if (!fresh) {
      setHistory(loadHistory(STORE, storageKey));
      return;
    }
    if (fresh.id === thread.id && thread.owner === storageKey) {
      setThread((prev) => ({ ...prev, messages: mergeMessages(fresh.messages, prev.messages) as Message[] }));
      setView('chat');
      return;
    }
    persistedLength.current.set(fresh.id, fresh.messages.length);
    setAnnouncement('');
    setThread({ id: fresh.id, startedAt: fresh.startedAt, owner: storageKey, messages: fresh.messages as Message[] });
    setCopiedIdx(null);
    setView('chat');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const historyListRef = useRef<HTMLUListElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const forget = (id: string) => {
    const index = history.findIndex((c) => c.id === id);
    const { list } = saveHistory(STORE, storageKey, (h) => removeConversation(h, id));
    setHistory(list);
    if (thread.id === id) setThread(newThread(storageKey));
    // Keep the keyboard user's place: the next row (or the previous), else the back button.
    setTimeout(() => {
      const rows = historyListRef.current?.querySelectorAll<HTMLButtonElement>('button[data-reopen]');
      const target = rows && rows.length ? rows[Math.min(Math.max(index, 0), rows.length - 1)] : backButtonRef.current;
      target?.focus();
    }, 0);
  };

  const copyMessage = (idx: number, text: string) => {
    navigator.clipboard?.writeText(text).then(
      () => {
        setCopiedIdx(idx);
        setTimeout(() => setCopiedIdx((v) => (v === idx ? null : v)), 1500);
      },
      () => { /* clipboard blocked — no-op */ },
    );
  };

  // In wide mode, centre and cap the reading measure for text; tables stay scrollable.
  const measureClass = expanded ? 'max-w-3xl mx-auto w-full' : '';
  const now = new Date();

  return (
    <>
      {/* SPEC-114: dimming backdrop behind the wide reading pane (desktop only). */}
      {open && expanded && (
        <div
          className="hidden md:block fixed inset-0 z-20 bg-black/50 transition-opacity duration-300 motion-reduce:transition-none"
          onClick={() => setExpanded(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar panel. Closed = invisible as well as off-screen, so its controls leave the
          tab order and the accessibility tree. */}
      <section
        ref={panelRef}
        aria-label="SuryaIQ assistant"
        aria-hidden={!open}
        className={`fixed top-0 right-0 bottom-0 z-30 bg-[var(--bg-raised)] border-l flex flex-col transition-all duration-300 ease-in-out motion-reduce:transition-none ${
          expanded ? 'w-full md:w-[min(880px,65vw)]' : 'w-[380px] md:w-[480px]'
        } ${
          plannerActive ? 'border-amber-500/40' : 'border-[var(--border-subtle)]'
        } ${open ? 'translate-x-0 visible' : 'translate-x-full invisible'}`}
      >
        {/* SPEC-114: accent bar makes the active "brain" obvious. */}
        <div className={`h-0.5 w-full ${plannerActive ? 'bg-amber-500' : 'bg-transparent'}`} />

        {/* Header: the SuryaMark + wordmark, the mode chip, then the controls. */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[var(--border-subtle)]">
          <div className="flex items-center gap-2.5 min-w-0">
            <SuryaMark size={22} className="text-[var(--text-primary)] shrink-0" />
            <h2 className="text-base font-display font-bold tracking-tight text-[var(--text-primary)] leading-none">
              SuryaIQ
            </h2>
            {canPlanner ? (
              <button
                type="button"
                onClick={() => setPlannerMode((v) => !v)}
                aria-pressed={plannerMode}
                className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] font-medium border transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60 ${
                  plannerMode
                    ? 'bg-amber-500/15 text-amber-400 border-amber-500/40'
                    : 'text-[var(--text-secondary)] border-[var(--border-default)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)]'
                }`}
                title={plannerMode
                  ? 'Planner mode is on: explains forecasts, classification and metrics with sources (read-only). Select to turn it off.'
                  : 'Planner mode is off (general answers). Select to turn it on: cited answers on forecasts, classification and metrics.'}
              >
                <BookOpen className="w-3 h-3" aria-hidden="true" />
                Planner mode
              </button>
            ) : (
              <span className="inline-flex items-center h-6 px-2 rounded-full text-[11px] font-medium border border-[var(--border-subtle)] text-[var(--text-secondary)]">
                General
              </span>
            )}
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              type="button"
              onClick={showHistory}
              aria-pressed={view === 'history'}
              aria-label="Previous chats"
              title="Previous chats"
              className={`${ICON_BUTTON} ${view === 'history' ? 'text-[var(--text-primary)] bg-[var(--bg-overlay)]' : ''}`}
            >
              <DrawerMark id="history" />
            </button>
            {/* SPEC-114: expand / collapse wide reading mode (desktop only). */}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className={`hidden md:grid ${ICON_BUTTON}`}
              title={expanded ? 'Collapse panel (Esc)' : 'Expand panel'}
              aria-label={expanded ? 'Collapse panel' : 'Expand panel'}
            >
              <DrawerMark id={expanded ? 'collapse' : 'expand'} />
            </button>
            {messages.length > 0 && view === 'chat' && (
              <button
                type="button"
                onClick={clearChat}
                className={`${ICON_BUTTON} hover:text-red-400`}
                title="Clear this chat (it stays in Previous chats)"
                aria-label="Clear this chat"
              >
                <DrawerMark id="clear" />
              </button>
            )}
            <button
              type="button"
              onClick={onToggle}
              className={ICON_BUTTON}
              title="Close panel (Esc)"
              aria-label="Close the assistant"
            >
              <DrawerMark id="close" />
            </button>
          </div>
        </div>

        {view === 'history' ? (
          <div className="flex-1 overflow-y-auto p-4">
            <div className={measureClass}>
              <div className="flex items-center gap-2 mb-3">
                <button
                  ref={backButtonRef}
                  type="button"
                  onClick={() => setView('chat')}
                  className={ICON_BUTTON}
                  aria-label="Back to the chat"
                  title="Back to the chat"
                >
                  <DrawerMark id="back" />
                </button>
                <h3 className="text-sm font-semibold text-[var(--text-primary)]">Previous chats</h3>
                <span className="ml-auto text-[11px] text-[var(--text-tertiary)] tabular-nums">
                  {history.length} saved on this device
                </span>
              </div>
              {history.length === 0 ? (
                <p className="text-sm text-[var(--text-secondary)] py-8 text-center">
                  No previous chats yet. Each conversation is saved here when you ask a question.
                </p>
              ) : (
                <ul ref={historyListRef} className="space-y-1.5">
                  {history.map((c) => {
                    const questions = c.messages.filter((m) => m.role === 'user').length;
                    const current = c.id === thread.id;
                    return (
                      <li
                        key={c.id}
                        className="group flex items-stretch rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-surface)] hover:border-[var(--border-default)]"
                      >
                        <button
                          type="button"
                          data-reopen
                          onClick={() => reopen(c)}
                          className="flex-1 min-w-0 text-left px-3 py-2.5 rounded-l-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60"
                        >
                          <span className="block text-sm text-[var(--text-primary)] truncate">{c.title}</span>
                          <span className="block mt-0.5 text-[11px] text-[var(--text-tertiary)] tabular-nums">
                            {formatWhen(c.updatedAt || c.startedAt, now)}
                            {' · '}
                            {questions} {questions === 1 ? 'question' : 'questions'}
                            {current ? ' · open now' : ''}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => forget(c.id)}
                          className={`${ICON_BUTTON} self-center mr-1.5 hover:text-red-400`}
                          aria-label={`Delete the chat "${c.title}"`}
                          title="Delete this chat"
                        >
                          <DrawerMark id="clear" size={16} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        ) : (
          /* Messages */
          <div className="flex-1 overflow-y-auto p-4">
            <div className={`space-y-3 ${measureClass}`}>
            {messages.length === 0 && (
              <div className="space-y-5">
                <div className="pt-4 pb-1 flex flex-col items-center text-center">
                  <SuryaMark size={28} className="text-[var(--text-secondary)] mb-2.5" />
                  <p className="text-sm font-medium text-[var(--text-primary)]">
                    {plannerActive ? 'Ask how a forecast, class or metric was worked out' : 'Ask about any product or number'}
                  </p>
                  <p className="text-xs text-[var(--text-tertiary)] mt-1 max-w-[34ch]">
                    {plannerActive
                      ? 'Planner mode answers with the sources it used.'
                      : 'Live tools across stock, sales, supply chain, demand planning and finance.'}
                  </p>
                </div>
                <div className="space-y-3">
                  {suggestions.map((cat) => (
                    <div key={cat.category}>
                      <p className="text-[10px] font-medium text-[var(--text-tertiary)] uppercase tracking-wider mb-1.5">{cat.category}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {cat.questions.map((q) => (
                          <button
                            key={q}
                            type="button"
                            onClick={() => send(q)}
                            disabled={loadingHere}
                            className="text-xs text-[var(--text-secondary)] bg-[var(--bg-surface)] border border-[var(--border-subtle)] hover:border-[var(--border-default)] hover:text-[var(--text-primary)] px-2.5 py-1.5 rounded-lg transition-colors motion-reduce:transition-none text-left disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60"
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : ''}`}>
                <div className={`group rounded-xl tabular-nums ${
                  msg.role === 'user'
                    ? 'max-w-[85%] px-3 py-2 bg-amber-500/10 border border-amber-500/25 text-[var(--text-primary)]'
                    : 'w-full px-3.5 py-3 bg-[var(--bg-surface)] border border-[var(--border-subtle)] text-[var(--text-secondary)]'
                }`}>
                  {msg.role === 'user' ? (
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  ) : (
                    <MarkdownMessage content={msg.content} />
                  )}

                  {msg.data && (
                    <details className="mt-2">
                      <summary className="text-xs cursor-pointer text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]">View data</summary>
                      <pre className="text-xs mt-1 overflow-x-auto max-h-40 bg-[var(--bg-base)] border border-[var(--border-subtle)] p-2 rounded font-mono">
                        {JSON.stringify(msg.data, null, 2)}
                      </pre>
                    </details>
                  )}

                  {/* SPEC-114: tool citations as chips + per-answer copy. */}
                  {msg.role === 'assistant' && (
                    <div className="flex items-center flex-wrap gap-1.5 mt-2.5">
                      {msg.tools_used && msg.tools_used.length > 0 && msg.tools_used.map((t) => (
                        <span
                          key={t}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-[var(--bg-raised)] border border-[var(--border-subtle)] text-[10px] font-medium text-[var(--text-tertiary)]"
                          title={t}
                        >
                          <BookOpen className="w-2.5 h-2.5" aria-hidden="true" />
                          {prettyToolName(t)}
                        </span>
                      ))}
                      <button
                        type="button"
                        onClick={() => copyMessage(i, msg.content)}
                        className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] text-[var(--text-tertiary)] hover:text-amber-400 hover:bg-[var(--bg-raised)] transition-colors motion-reduce:transition-none md:opacity-0 md:group-hover:opacity-100 focus:opacity-100"
                        title="Copy answer"
                        aria-label="Copy answer"
                      >
                        {copiedIdx === i ? <Check className="w-2.5 h-2.5" /> : <Copy className="w-2.5 h-2.5" />}
                        {copiedIdx === i ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loadingHere && (
              <div className="rounded-xl px-3.5 py-3 bg-[var(--bg-surface)] border border-[var(--border-subtle)]" role="status">
                <div className="flex items-center gap-2">
                  <SuryaMark size={16} className="text-[var(--text-tertiary)] animate-pulse motion-reduce:animate-none" />
                  <span className="text-sm text-[var(--text-secondary)]">
                    {plannerActive ? 'Consulting tools' : 'Thinking'}
                  </span>
                  <span className="ml-auto text-[10px] font-mono text-[var(--text-tertiary)] tabular-nums">{elapsed}s</span>
                </div>
                <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-[var(--bg-raised)]">
                  <div className="h-full w-1/3 rounded-full bg-amber-500/50 animate-pulse motion-reduce:animate-none" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
            </div>
          </div>
        )}

        {/* One live region for the drawer: set only when a reply arrives (or fails). */}
        <p className="sr-only" aria-live="polite">{announcement}</p>

        {/* Composer, anchored at the bottom. */}
        <div className="border-t border-[var(--border-subtle)] p-3">
          <div className={measureClass}>
          <form onSubmit={(e) => { e.preventDefault(); setView('chat'); send(input); }} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={loadingHere ? 'Waiting for the answer…' : 'Ask about your data…'}
              aria-label="Ask SuryaIQ a question"
              className="flex-1 min-w-0 h-10 px-3 text-sm bg-[var(--bg-base)] border border-[var(--border-default)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/50"
              disabled={loadingHere}
            />
            <button
              type="submit"
              disabled={loadingHere || !input.trim()}
              aria-label="Send"
              title="Send"
              className="grid place-items-center w-10 h-10 shrink-0 bg-amber-500 text-[var(--text-inverse)] rounded-lg hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-raised)]"
            >
              <Send className="w-4 h-4" aria-hidden="true" />
            </button>
          </form>
          <p className="text-[10px] text-[var(--text-tertiary)] mt-2 text-center">
            <kbd className="px-1 py-0.5 border border-[var(--border-subtle)] rounded font-mono">/</kbd> open
            <span aria-hidden="true"> · </span>
            <kbd className="px-1 py-0.5 border border-[var(--border-subtle)] rounded font-mono">Esc</kbd> {expanded ? 'collapse' : 'close'}
          </p>
          </div>
        </div>
      </section>
    </>
  );
}
